import { useState } from "react";
import { ProjectBilling } from "types/project";

/**
 * Small € icon next to a project step when any of its controlpoints
 * are linked to a billing step. Uses the project's own billing data.
 *
 * Props:
 *   billing: the project's billing object (project.billing)
 *   controlpointIds: controlpoint IDs within this step
 */
export function BillingStepIndicator({
  billing,
  controlpointIds,
}: {
  billing?: ProjectBilling;
  controlpointIds: string[];
}) {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!billing || !controlpointIds.length) return null;

  const cpSet = new Set(controlpointIds);
  const linked = billing.steps.filter(
    (s) => s.linked_step_ids?.some((id) => cpSet.has(id)),
  );

  if (linked.length === 0) return null;

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center cursor-default">
        <span className="text-amber-600 text-h6 font-bold">€</span>
      </div>

      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 bg-white border rounded-lg shadow-lg px-3 py-2 whitespace-nowrap pointer-events-none">
          <div className="text-h5 font-semibold text-neutral-500 mb-1">
            Facturation liée
          </div>
          {linked.map((step, i) => {
            const statusLabels: Record<string, string> = {
              pending: "En attente", creating: "Création...", draft: "Brouillon",
              finalized: "Finalisée", sent: "Envoyée", paid: "Payée", error: "Erreur",
            };
            return (
              <div key={i} className="flex flex-col py-0.5">
                <div className="flex flex-row items-center gap-2">
                  <span className="text-h4 font-medium text-neutral-700">
                    {step.label}
                  </span>
                  <span className="text-h4 text-amber-600 font-semibold">
                    {step.montant.toLocaleString("fr-FR")} €
                  </span>
                </div>
                <span className="text-h5 text-neutral-400">
                  {step.percentage}% · {statusLabels[step.status] || step.status}
                </span>
              </div>
            );
          })}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-r border-b rotate-45 -mt-1" />
        </div>
      )}
    </div>
  );
}
